import {
	ordinalSuffix,
	getPlayerLink,
	formatNumberWithDecimal,
	formatGameTeamDetails,
	getTeamLogo,
	formatPlayerLink,
	sortByNumber,
	sortByPlayerElement,
	getPlayerWithoutLinkWithNumberAndPosition,
	getPlayerLinkWithNumberAndPosition,
	sortNumberText,
} from './shared_helpers';
import { env_url } from './endpoints';
import update from 'immutability-helper';
import { preserveQueryParams } from './browser_helpers';

import React from 'react';
import { formatTeamYardsInText, getPlayColor, shortenPlayerNames } from './football_helpers';

export const NFLsilhouette = `${env_url}/player-silhouette-hockey-cut.png`;

export const imgErrNFL = ev => {
	ev.target.src = NFLsilhouette;
};

export const nflTeamNameIDArrayMap = new Map([
	['e6aa13a4-0055-48a9-bc41-be28dc106929', 'atlanta-falcons'],
	['768c92aa-75ff-4a43-bcc0-f2798c2e1724', 'buffalo-bills'],
	['82d2d380-3834-4938-835f-aec541e5ece7', 'houston-texans'],
	['7b112545-38e6-483c-a55c-96cf6ee49cb8', 'chicago-bears'],
	['ad4ae08f-d808-42d5-a1e6-e9bc4e34d123', 'cincinnati-bengals'],
	['d5a2eb42-8065-4174-ab79-0a6fa820e35e', 'cleveland-browns'],
	['e627eec7-bbae-4fa4-8e73-8e1d6bc5c060', 'dallas-cowboys'],
	['ce92bd47-93d5-4fe9-ada4-0fc681e6caa0', 'denver-broncos'],
	['c5a59daa-53a7-4de0-851f-fb12be893e9e', 'detroit-lions'],
	['a20471b4-a8d9-40c7-95ad-90cc30e46932', 'green-bay-packers'],
	['d26a1ca5-722d-4274-8f97-c92e49c96315', 'tennessee-titans'],
	['82cf9565-6eb9-4f01-bdbd-5aa0d472fcd9', 'indianapolis-colts'],
	['6680d28d-d4d2-49f6-aace-5292d3ec02c2', 'kansas-city-chiefs'],
	['7d4fcc64-9cb5-4d1b-8e75-8a906d1e1576', 'las-vegas-raiders'],
	['2eff2a03-54d4-46ba-890e-2bc3925548f3', 'los-angeles-rams'],
	['4809ecb0-abd3-451d-9c4a-92a90b83ca06', 'miami-dolphins'],
	['33405046-04ee-4058-a950-d606f8c30852', 'minnesota-vikings'],
	['97354895-8c77-4fd4-a860-32e62ea7382a', 'new-england-patriots'],
	['0d855753-ea21-4953-89f9-0e20aff9eb73', 'new-orleans-saints'],
	['04aa1c9d-66da-489d-b16a-1dee3f2eec4d', 'new-york-giants'],
	['5fee86ae-74ab-4bdd-8416-42a9dd9964f3', 'new-york-jets'],
	['386bdbf9-9eea-4869-bb9a-274b0bc66e80', 'philadelphia-eagles'],
	['de760528-1dc0-416a-a978-b510d20692ff', 'arizona-cardinals'],
	['cb2f9f1f-ac67-424e-9e72-1475cb0ed398', 'pittsburgh-steelers'],
	['1f6dcffb-9823-43cd-9ff4-e7a8466749b5', 'los-angeles-chargers'],
	['f0e724b0-4cbf-495a-be47-013907608da9', 'san-francisco-49ers'],
	['3d08af9e-c767-4f88-a7dc-b920c6d2b4a8', 'seattle-seahawks'],
	['4254d319-1bc7-4f81-b4ab-b5e6f3402b69', 'tampa-bay-buccaneers'],
	['22052ff7-c065-42ee-bc8f-c4691c50e624', 'washington-commanders'],
	['f14bf5cc-9a82-4a38-bc15-d39f75ed5314', 'carolina-panthers'],
	['f7ddd7fa-0bae-4f90-bc8e-669e4d6cf2de', 'jacksonville-jaguars'],
	['ebd87119-b331-4469-9ea6-d51fe3ce2f1c', 'baltimore-ravens'],
]);

export const labelQuarters = (quarters = []) => {
	let quartersCopy = quarters.slice(0);

	quartersCopy.forEach((quarter, i) => {
		if (i < 4) {
			quarter.label = i + 1;
		} else if (i === 4) {
			quarter.label = 'OT';
		} else {
			quarter.label = i - 3 + 'OT';
		}
	});

	return quartersCopy;
};

export const labelQuartersFullName = (quarters = []) => {
	let quartersCopy = quarters.slice(0);

	quartersCopy.forEach((quarter, i) => {
		if (i < 4) {
			quarter.label = ordinalSuffix(i + 1) + ' Quarter';
		} else if (i === 4) {
			quarter.label = 'OT';
		} else {
			quarter.label = i - 3 + 'OT';
		}
	});

	return quartersCopy;
};

export function formatSeasonLeaders(homeTeam, visitingTeam) {
	const leaderCategories = [
		{
			title: 'Passing Yards',
			categoryKey: 'passing_leader',
			valueKey: 'yards',
		},
		{
			title: 'Rushing Yards',
			categoryKey: 'rushing_leader',
			valueKey: 'rushing_yards', // check if there in a rushing_yards value to handle cases where the QB is a passing leader as well as a rushing leader.
			alternateValueKey: 'yards',
		},
		{
			title: 'Receiving Yards',
			categoryKey: 'receiving_leader',
			valueKey: 'yards',
		},
		{
			title: 'Field Goals Made',
			categoryKey: 'field_goals_made_leader',
			valueKey: 'field_goals_made',
		},
		{
			title: 'Tackles',
			categoryKey: 'tackles_leader',
			valueKey: 'tackles',
		},
		{
			title: 'Sacks',
			categoryKey: 'sacks_leader',
			valueKey: 'sacks',
			decimalPlaces: 1,
		},
		{
			title: 'Interceptions',
			categoryKey: 'interceptions_leader',
			valueKey: 'interceptions',
		},
		{
			title: 'Forced Fumbles',
			categoryKey: 'forced_fumbles_leader',
			valueKey: 'forced_fumbles',
		},
	];

	const seasonLeaders = leaderCategories.map(leaderCategory => {
		const homeTeamData = homeTeam?.season_leaders?.[leaderCategory.categoryKey];
		const visitingTeamData = visitingTeam?.season_leaders?.[leaderCategory.categoryKey];

		if (!homeTeamData || !visitingTeamData) {
			return null;
		}

		let homeTeamValue = homeTeamData[leaderCategory.valueKey];
		let visitingTeamValue = visitingTeamData[leaderCategory.valueKey];

		if (leaderCategory.alternateValueKey) {
			homeTeamValue = homeTeamValue || homeTeamData[leaderCategory.alternateValueKey];
			visitingTeamValue = visitingTeamValue || visitingTeamData[leaderCategory.alternateValueKey];
		}

		if (homeTeamValue && visitingTeamValue && leaderCategory.decimalPlaces) {
			homeTeamValue = homeTeamValue.toFixed(leaderCategory.decimalPlaces);
			visitingTeamValue = visitingTeamValue.toFixed(leaderCategory.decimalPlaces);
		}

		return {
			title: leaderCategory.title,
			homeTeamShortName: homeTeam.short_name,
			homeTeamColor: homeTeam.color,
			visitingTeamColor: visitingTeam.color,
			visitingTeamShortName: visitingTeam.short_name,
			homeTeamPlayerImage: homeTeamData.image_urls.xs,
			visitingTeamPlayerImage: visitingTeamData.image_urls.xs,
			homeTeamLogo: homeTeam.image_url_90,
			visitingTeamLogo: visitingTeam.image_url_90,
			homeTeamValue: homeTeamValue,
			visitingTeamValue: visitingTeamValue,
			homeTeamPlayerName: `${homeTeamData.first_name.charAt(0)}. ${homeTeamData.last_name}`,
			visitingTeamPlayerName: `${visitingTeamData.first_name.charAt(0)}. ${visitingTeamData.last_name}`,
			homeTeamDetails: {
				first_name: homeTeamData.first_name,
				last_name: homeTeamData.last_name,
				id: homeTeamData.id,
			},
			visitingTeamDetails: {
				first_name: visitingTeamData.first_name,
				last_name: visitingTeamData.last_name,
				id: visitingTeamData.id,
			},
		};
	});
	return seasonLeaders.filter(n => n); // filter out null values
}

export function formatTeamStats(homeTeam, visitingTeam) {
	if (!visitingTeam.season_stats || !homeTeam.season_stats) {
		return [];
	}

	const statCategories = [
		{
			title: 'Passing Yards Per Game',
			valueFormatter: n => formatNumberWithDecimal(n, 1),
			valueKey: 'passing_yards_per_game',
		},
		{
			title: 'Rushing Yards Per Game',
			valueFormatter: n => formatNumberWithDecimal(n, 1),
			valueKey: 'rushing_yards_per_game',
		},
		{
			title: 'Points Per Game',
			valueFormatter: n => formatNumberWithDecimal(n, 1),
			valueKey: 'points_scored_per_game',
		},
		{
			title: 'Points Allowed Per Game',
			valueFormatter: n => formatNumberWithDecimal(n, 1),
			valueKey: 'points_allowed_per_game',
		},
		{ title: 'Touchdowns', valueKey: 'touchdowns' },
		{
			title: 'Scoring Efficiency',
			valueKey: 'scoring_efficiency_percentage',
			valueFormatter: n => `${formatNumberWithDecimal(n, 1)}%`,
		},
		{
			title: 'Third Down Efficiency',
			valueKey: 'third_down_efficiency_percentage',
			valueFormatter: n => `${formatNumberWithDecimal(n, 1)}%`,
		},
		{
			title: '300 Yard Passing Games',
			valueKey: 'three_hundred_yard_games_passing',
		},
		{
			title: '100 Yard Rushing Games',
			valueKey: 'hundred_yard_games_rushing',
		},
		{
			title: '100 Yard Receiving Games',
			valueKey: 'hundred_yard_games_receiving',
		},
		{
			title: 'Takeaways',
			valueKey: 'takeaways',
		},

		{ title: 'Interceptions', valueKey: 'interceptions' },
		{ title: 'Sacks', valueKey: 'sacks' },
	];

	const teamStats = statCategories.map(statCategory => {
		let homeTeamValue = homeTeam.season_stats[statCategory.valueKey];
		let visitingTeamValue = visitingTeam.season_stats[statCategory.valueKey];

		if (!homeTeamValue || !visitingTeamValue) {
			return null;
		}

		return {
			title: statCategory.title,
			homeTeamValue: homeTeamValue,
			visitingTeamValue: visitingTeamValue,
			valueFormatter: statCategory.valueFormatter,
		};
	});
	return teamStats.filter(n => n); // filter out null values
}
export function formatGameStats(homeTeam, visitingTeam) {
	if (!visitingTeam.game_stats || !homeTeam.game_stats) {
		return [];
	}

	const statCategories = [
		{
			title: 'Total Yards',
			valueKey: 'total_yards',
		},
		{
			title: 'Passing Yards',
			valueKey: 'passing_yards',
		},
		{
			title: 'Rushing Yards',
			valueKey: 'rushing_yards',
		},
		{
			title: 'Total Plays',
			valueKey: 'game_total_plays',
		},
		{
			title: 'Avg. Yards per Play',
			valueKey: 'game_total_average',
			valueFormatter: n => formatNumberWithDecimal(n, 1),
		},
		{
			title: 'First Downs',
			valueKey: 'first_downs',
		},
		{
			title: 'Third Down Efficiency',
			valueKey: 'third_down_efficiency_percentage',
			valueFormatter: n => `${n}%`,
		},
		(visitingTeam.game_stats.penalties || homeTeam.game_stats.penalties) && {
			title: 'Penalties',
			valueKey: 'penalties',
		},
		(visitingTeam.game_stats.penalty_yards || homeTeam.game_stats.penalty_yards) && {
			title: 'Penalty Yards',
			valueKey: 'penalty_yards',
		},
		{ title: 'Turnovers', valueKey: 'turnovers' },
		{ title: 'Tackles', valueKey: 'tackles' },
		{
			title: 'QB Sacks',
			valueKey: 'sacks',
		},
		{
			title: 'Sack Yards Lost',
			valueKey: 'yards_lost',
		},
		{ title: 'Time of Possession', valueKey: 'possession_time' },
	];

	const gameStats = statCategories.map(statCategory => {
		let homeTeamValue = homeTeam.game_stats[statCategory.valueKey];
		let visitingTeamValue = visitingTeam.game_stats[statCategory.valueKey];

		if (typeof homeTeamValue === 'string' || homeTeamValue instanceof String) {
			homeTeamValue = parseFloat(homeTeamValue) || 0;
		}
		if (typeof visitingTeamValue === 'string' || visitingTeamValue instanceof String) {
			visitingTeamValue = parseFloat(visitingTeamValue) || 0;
		}

		if (statCategory.valueKey === 'possession_time') {
			homeTeamValue = `${homeTeam.game_stats.possession_minutes}:${homeTeam.game_stats.possession_seconds}`;
			visitingTeamValue = `${visitingTeam.game_stats.possession_minutes}:${visitingTeam.game_stats.possession_seconds}`;
		}

		if ((!homeTeamValue && homeTeamValue !== 0) || (!visitingTeamValue && visitingTeamValue !== 0)) {
			return null;
		}

		return {
			title: statCategory.title,
			homeTeamValue: homeTeamValue,
			visitingTeamValue: visitingTeamValue,
			valueFormatter: statCategory.valueFormatter,
		};
	});
	return gameStats.filter(n => n); // filter out null values
}
export function formatLastNineEvents(lastNineEvents = [], homeTeam, visitingTeam) {
	const ghostPlays = [
		{ id: 1, isGhost: true },
		{ id: 2, isGhost: true },
		{ id: 3, isGhost: true },
		{ id: 4, isGhost: true },
		{ id: 5, isGhost: true },
		{ id: 6, isGhost: true },
		{ id: 7, isGhost: true },
		{ id: 8, isGhost: true },
		{ id: 9, isGhost: true },
	];

	if (!lastNineEvents) return ghostPlays;

	const massagedPlays = lastNineEvents.map(play => {
		const formattedPlayDescription = formatTeamYardsInText(play.description);
		let teamLogo =
			play.possession === homeTeam.short_name
				? getTeamLogo(homeTeam, true, 'image_url_90')
				: getTeamLogo(visitingTeam, true, 'image_url_90');

		if (['timeout', 'tv_timeout', 'two_minute_warning', 'period_end'].includes(play.play_type)) teamLogo = null;

		const concatPlays = ['field_goal', 'extra_point_attempt', 'two_point_attempt', 'rush', 'first_down'];
		if (play?.play_type && concatPlays.includes(play.play_type)) {
			play.play_type = play.play_type + ' ' + play.result;
		}

		return {
			id: play.id,
			title: setPlayTitle(play),
			color: getPlayColor(play),
			bodyText: shortenPlayerNames(formattedPlayDescription, play.players),
			teamLogo: teamLogo,
		};
	});

	return massagedPlays.concat(ghostPlays.slice(massagedPlays.length));
}
export function formatBoxscore(team = {}, withPlayerLinks = true) {
	let {
		passing = [],
		rushing = [],
		receiving = [],
		kickings = [],
		punts = [],
		kick_returns = [],
		punt_returns = [],
		defense = [],
	} = team;
	defense = defense.filter(obj => {
		return obj.tackles || obj.sacks || obj.passes_defensed || obj.forced_fumbles;
	});

	// Sorting the table data
	passing = passing.length > 1 ? passing.sort((a, b) => b.yards - a.yards) : passing;
	rushing = rushing.length > 1 ? rushing.sort((a, b) => b.yards - a.yards) : rushing;
	receiving = receiving.length > 1 ? receiving.sort((a, b) => b.yards - a.yards) : receiving;
	punts = punts.length > 1 ? punts.sort((a, b) => b.yards - a.yards) : punts;
	kick_returns = kick_returns.length > 1 ? kick_returns.sort((a, b) => b.yards - a.yards) : kick_returns;
	punt_returns = punt_returns.length > 1 ? punt_returns.sort((a, b) => b.yards - a.yards) : punt_returns;
	defense = defense.length > 1 ? defense.sort((a, b) => b.tackles - a.tackles) : defense;

	return [
		{
			title: 'Passing',
			columns: [
				{
					title: 'COMP/ATT',
					dataKey: 'passesCompletedAttempted',
					tooltipText: 'Passes Completed/Passes Attempted',
				},
				{ title: 'YDS', dataKey: 'yards', tooltipText: 'Yards' },
				{ title: 'TD', dataKey: 'touchdowns', tooltipText: 'Touchdowns' },
				{ title: 'INT', dataKey: 'interceptions', tooltipText: 'Interceptions' },
				{},
				{},
			],
			data: passing.map(player => {
				return {
					name: withPlayerLinks
						? getPlayerLinkWithNumberAndPosition(player)
						: getPlayerWithoutLinkWithNumberAndPosition(player),
					passesCompletedAttempted: `${player.passes_completed}/${player.passes_attempted}`,
					yards: player.yards,
					touchdowns: player.touchdowns,
					interceptions: player.interceptions,
				};
			}),
		},
		{
			title: 'Rushing',
			columns: [
				{ title: 'ATT', dataKey: 'rushesAttempted', tooltipText: 'Rushes attempted' },
				{ title: 'YDS', dataKey: 'yards', tooltipText: 'Yards' },
				{ title: 'TD', dataKey: 'touchdowns', tooltipText: 'Touchdowns' },
				{
					title: 'AVG',
					dataKey: 'average',
					tooltipText: 'Average',
					sortFunction: sortNumberText,
				},
				{ title: 'LNG', dataKey: 'longestRush', tooltipText: 'Longest Rush' },
				{},
			],
			data: rushing.map(player => {
				return {
					name: withPlayerLinks
						? getPlayerLinkWithNumberAndPosition(player)
						: getPlayerWithoutLinkWithNumberAndPosition(player),
					rushesAttempted: player.rushes_attempted,
					yards: player.yards,
					average: player.average,
					touchdowns: player.touchdowns,
					longestRush: player.longest_rush,
				};
			}),
		},
		{
			title: 'Receiving',
			columns: [
				{ title: 'REC', dataKey: 'receptions', tooltipText: 'Receptions' },
				{ title: 'YDS', dataKey: 'yards', tooltipText: 'Yards' },
				{ title: 'TD', dataKey: 'touchdowns', tooltipText: 'Touchdowns' },
				{
					title: 'AVG',
					dataKey: 'average',
					tooltipText: 'Average',
					sortFunction: sortNumberText,
				},
				{ title: 'LNG', dataKey: 'longestReceiving', tooltipText: 'Longest receiving' },
				{ title: 'TGT', dataKey: 'targets', tooltipText: 'Target' },
			],
			data: receiving.map(player => {
				return {
					name: withPlayerLinks
						? getPlayerLinkWithNumberAndPosition(player)
						: getPlayerWithoutLinkWithNumberAndPosition(player),
					targets: player.targets,
					receptions: player.receptions,
					yards: player.yards,
					average: player.average,
					touchdowns: player.touchdowns,
					longestReceiving: player.longest_receiving,
				};
			}),
		},
		{
			title: 'Kicking',
			columns: [
				{ title: 'FG', dataKey: 'made', tooltipText: 'Made' },
				{ title: 'LNG', dataKey: 'longestkick', tooltipText: 'Longest Kick' },
				{ title: 'XP', dataKey: 'extraPoints', tooltipText: 'Targets' },
				{},
				{},
				{},
			],
			data: kickings.map(player => {
				return {
					name: withPlayerLinks
						? getPlayerLinkWithNumberAndPosition(player)
						: getPlayerWithoutLinkWithNumberAndPosition(player),
					made: player.made,
					longestkick: player.longest_kick,
					extraPoints: formatExtraPoints(player),
				};
			}),
		},
		{
			title: 'Punting',
			columns: [
				{ title: 'P', dataKey: 'punts', tooltipText: 'Punts' },
				{ title: 'YDS', dataKey: 'yards', tooltipText: 'Yards' },
				{
					title: 'AVG',
					dataKey: 'average',
					tooltipText: 'Average',
					sortFunction: sortNumberText,
				},
				{
					title: 'NET AVG',
					dataKey: 'netAverage',
					tooltipText: 'Net Average',
					sortFunction: sortNumberText,
				},
				{ title: 'LNG', dataKey: 'longestPunt', tooltipText: 'Longest Punt' },
				{},
			],
			data: punts.map(player => {
				return {
					name: withPlayerLinks
						? getPlayerLinkWithNumberAndPosition(player)
						: getPlayerWithoutLinkWithNumberAndPosition(player),
					punts: player.punts,
					yards: player.yards,
					average: player.average,
					netAverage: player.net_average,
					longestPunt: player.longest_punt,
				};
			}),
		},
		{
			title: 'Kick Returns',
			columns: [
				{ title: 'KR', dataKey: 'returns', tooltipText: 'Returns' },
				{ title: 'YDS', dataKey: 'yards', tooltipText: 'Yards' },
				{
					title: 'AVG',
					dataKey: 'average',
					tooltipText: 'Average',
					sortFunction: sortNumberText,
				},
				{
					title: 'LNG',
					dataKey: 'longestKickReturn',
					tooltipText: 'Longest Kick Return',
				},
				{},
				{},
			],
			data: kick_returns.map(player => {
				return {
					name: withPlayerLinks
						? getPlayerLinkWithNumberAndPosition(player)
						: getPlayerWithoutLinkWithNumberAndPosition(player),
					returns: player.returns,
					yards: player.yards,
					average: player.average,
					longestKickReturn: player.longest_kick_return,
				};
			}),
		},

		{
			title: 'Punt Returns',
			columns: [
				{ title: 'PR', dataKey: 'returns', tooltipText: 'Returns' },
				{ title: 'YDS', dataKey: 'yards', tooltipText: 'Yards' },
				{
					title: 'AVG',
					dataKey: 'average',
					tooltipText: 'Average',
					sortFunction: sortNumberText,
				},
				{ title: 'LNG', dataKey: 'longestPuntReturn', tooltipText: 'Longest Punt Return' },
				{},
				{},
			],
			data: punt_returns.map(player => {
				return {
					name: withPlayerLinks
						? getPlayerLinkWithNumberAndPosition(player)
						: getPlayerWithoutLinkWithNumberAndPosition(player),
					returns: player.returns,
					yards: player.yards,
					average: player.average,
					longestPuntReturn: player.longest_punt_return,
				};
			}),
		},
		{
			title: 'Defense',
			columns: [
				{ title: 'TCKL', dataKey: 'tackles', tooltipText: 'Tackles' },
				{ title: 'SACK', dataKey: 'sacks', tooltipText: 'Sacks' },
				{ title: 'INT', dataKey: 'interceptions', tooltipText: 'Interceptions' },
				{ title: 'PDEF', dataKey: 'passesDefensed', tooltipText: 'Passes Defensed' },
				{ title: 'FF', dataKey: 'forcedFumbles', tooltipText: 'Forced Fumbles' },
				{},
			],
			data: defense.map(player => {
				return {
					name: withPlayerLinks
						? getPlayerLinkWithNumberAndPosition(player)
						: getPlayerWithoutLinkWithNumberAndPosition(player),
					tackles: player.tackles,
					sacks: player.sacks,
					interceptions: player.interceptions,
					passesDefensed: player.passes_defensed,
					forcedFumbles: player.forced_fumbles,
				};
			}),
		},
	];
}

export function formatInjuries(teamInjuries = []) {
	const formattedInjuries = teamInjuries.map(injury => {
		return {
			playerName: getPlayerLink(injury),
			playerPosition: injury.position,
			status: injury.status,
			type: `${injury.type.charAt(0).toUpperCase()}${injury.type.slice(1)}`,
			timestamp: injury.timestamp,
			emptyColumn: ' ',
		};
	});

	return formattedInjuries.sort((a, b) => b.timestamp - a.timestamp);
}

export function formatGameAlert(down, distance, ballLocation) {
	if (!down || !distance || !ballLocation) {
		return null;
	} else {
		return `${ordinalSuffix(down)} and ${distance} on ${ballLocation.toUpperCase()}`;
	}
}

function formatExtraPoints(playerData) {
	if (playerData.extra_points_made && playerData.extra_points_attempted) {
		return `${playerData.extra_points_made}/${playerData.extra_points_attempted}`;
	} else {
		return null;
	}
}

function setPlayTitle(play) {
	const eventNameMap = new Map([
		['kick_off_return', 'KICKOFF RETURN'],
		['kick_off', 'KICKOFF'],
		['touchback', 'TOUCHBACK'],
		['extra_point_attempt good', 'EXTRA POINT'],
		['extra_point_attempt no good', 'MISSED EXTRA POINT'],
		['extra_point_attempt blocked', 'MISSED EXTRA POINT'],
		['two_point_attempt good', 'TWO POINT CONVERSION'],
		['two_point_attempt no good', 'MISSED TWO POINT CONVERSION'],
		['onside_kick_off', 'ONSIDE KICKOFF'],
		['timeout', 'TIMEOUT'],
		['tv_timeout', 'TIMEOUT'],
		['two_minute_warning', 'TWO MINUTE WARNING'],
		['period_end', `End of ${ordinalSuffix(play.quarter)} Quarter`],
	]);
	let formattedEvent = eventNameMap.has(play.play_type) && eventNameMap.get(play.play_type);
	const yardlineTeamRegex = play.yardline.match(/[a-zA-Z]+/g) || [];
	const yardLineDistanceRegex = play.yardline.match(/[0-9]+/g);
	const yardLineTeam = (yardlineTeamRegex[0] || '').toUpperCase();
	const yardLineDistance = yardLineDistanceRegex[0] || '';

	if (formattedEvent) {
		return formattedEvent;
	} else {
		return `${ordinalSuffix(play.down).toUpperCase()} & ${play.distance} / ${yardLineTeam} ${yardLineDistance}`;
	}
}

export function formatSeasonRecord(team) {
	var wins = team?.season_stats?.wins ? team?.season_stats?.wins : 0;
	var losses = team?.season_stats?.losses ? team?.season_stats?.losses : 0;
	var ties = team?.season_stats?.ties ? team?.season_stats?.ties : 0;
	return wins + '-' + losses + '-' + ties;
}

export function formatTeamRecord(team) {
	const teamDivision = team.division || {};

	if (teamDivision.rank && teamDivision.short_name) {
		return `${ordinalSuffix(teamDivision.rank)}  ${teamDivision.short_name}`;
	} else {
		return null;
	}
}

export function formatQuarters(quarters) {
	const quarterTotal = 4;
	let defaultQuarters = [];
	for (let i = 0; i < quarterTotal; i++) {
		defaultQuarters = [
			...defaultQuarters,
			{
				visitingTeamScore: '-',
				homeTeamScore: '-',
				label: '',
			},
		];
	}
	let filledQuarters = quarters ? update(defaultQuarters, { $merge: quarters }) : [];
	let labelledQuarters = labelQuarters(filledQuarters);

	return [filledQuarters, labelledQuarters];
}

export function isGameTypePostseason(gameTypeID) {
	switch (gameTypeID) {
		case 3:
			return true;
		default:
			return false;
	}
}

export const getSeasonTypeFromGameType = gameType => {
	if (gameType === 'Exhibition') {
		return 'pre';
	} else {
		return 'reg';
	}
};
const offenseLegendItems = [
	{
		name: 'GP',
		description: 'Games Played',
	},
	{
		name: 'COMP',
		description: 'Competitions',
	},
	{
		name: 'ATT',
		description: 'Attempts',
	},
	{
		name: 'CP%',
		description: 'Completion Percentage',
	},
	{
		name: 'YDS',
		description: 'Total Yards',
	},
	{
		name: 'AVG',
		description: 'Avg. Yards per Attempt/Reception',
	},
	{
		name: 'YDS/G',
		description: 'Yards Per Game',
	},
	{
		name: 'LNG',
		description: 'Longest Pass/Rush/Reception',
	},
	{
		name: 'TD',
		description: 'Touchdowns',
	},
	{
		name: 'INT',
		description: 'Interceptions',
	},
	{
		name: 'SACK',
		description: 'Times Sacked',
	},
	{
		name: 'QBR',
		description: 'Quarterback Rating',
	},
	{
		name: 'F-FL',
		description: 'Fumbles-Fumbles Lost',
	},
	{
		name: 'FD',
		description: 'First Downs',
	},
	{
		name: 'REC',
		description: 'Receptions',
	},
	{
		name: 'TGTS',
		description: 'Targets',
	},
];

const defenseLegendItems = [
	{
		name: 'SOLO',
		description: 'Solo Tackles',
	},
	{
		name: 'AST',
		description: 'Tackle Assists',
	},
	{
		name: 'TOTAL',
		description: 'Tackles (Solo + Assists)',
	},
	{
		name: 'SACK',
		description: 'Sacks',
	},
	{
		name: 'INT',
		description: 'Interceptions',
	},
	{
		name: 'PDEF',
		description: 'Passes Defended',
	},
	{
		name: 'FF',
		description: 'Forced Fumbles',
	},
	{
		name: 'REC',
		description: 'Fumbles Recovered',
	},
];

const kickingLegendItems = [
	{
		name: 'FGM-FGA',
		description: 'Field Goals Made-Attempted',
	},
	{
		name: 'PCT',
		description: 'Field Goal Percentage',
	},
	{
		name: 'LNG',
		description: 'Longest Field Goal/Punt',
	},
	{
		name: '50+',
		description: '50+ Yard Field Goals',
	},
	{
		name: 'XPM-XPA',
		description: 'Extra Point Kicks Made-Attempted',
	},
	{
		name: 'XP%',
		description: 'Extra Point Percentage',
	},
	{
		name: 'P',
		description: 'Punts',
	},
	{
		name: 'NET',
		description: 'Net Punt Yards',
	},
	{
		name: 'TB',
		description: 'Touchbacks',
	},
	{
		name: 'FC',
		description: 'Fair Catches',
	},
	{
		name: 'IN20',
		description: 'Kicks Downed Inside 20 Yard Line',
	},
];

const returningLegendItems = [
	{
		name: 'PR',
		description: 'Punt Returns',
	},
	{
		name: 'KR',
		description: 'Kickoff Returns',
	},
	{
		name: 'YDS',
		description: 'Punt/Kickoff Return Yards',
	},
	{
		name: 'TD',
		description: 'Punt/Kickoff Return Touchdowns',
	},
	{
		name: 'LNG',
		description: 'Longest Punt/Kickoff Return',
	},
	{
		name: 'AVG',
		description: 'Punt/Kickoff Return Average',
	},
];

export const statsLegendSections = [
	{
		title: 'Offense',
		items: offenseLegendItems,
	},
	{
		title: 'Defense',
		items: defenseLegendItems,
	},
	{
		title: 'Kicking / Punting',
		items: kickingLegendItems,
	},
	{
		title: 'Returning',
		items: returningLegendItems,
	},
];

function formatPassingStats(stats = []) {
	const passingColumns = [
		{
			title: 'PLAYER',
			dataKey: 'player',
			textAlign: 'left',
			cellWidthOverride: '20',
			customSortFunctionAscending: (a, b) => sortByPlayerElement(a.player, b.player),
			customSortFunctionDescending: (a, b) => sortByPlayerElement(b.player, a.player),
			initialSortDirection: 'asc',
			sticky: true,
		},
		{
			title: 'GP',
			dataKey: 'gamesPlayed',
			textAlign: 'center',
			cellWidthOverride: '6',
			initialSortDirection: 'desc',
		},
		{
			title: 'COMP',
			dataKey: 'completions',
			textAlign: 'center',
			cellWidthOverride: '6',
			initialSortDirection: 'desc',
		},
		{
			title: 'ATT',
			dataKey: 'attempts',
			textAlign: 'center',
			cellWidthOverride: '6',
			initialSortDirection: 'desc',
		},
		{
			title: 'CP%',
			dataKey: 'completionPct',
			textAlign: 'center',
			cellWidthOverride: '6',
			initialSortDirection: 'desc',
		},
		{
			title: 'YDS',
			dataKey: 'passingYards',
			textAlign: 'center',
			cellWidthOverride: '6',
			initialSortDirection: 'desc',
		},
		{
			title: 'AVG',
			dataKey: 'averageYards',
			textAlign: 'center',
			cellWidthOverride: '6',
			initialSortDirection: 'desc',
		},
		{
			title: 'YDS/G',
			dataKey: 'yardsPerGame',
			textAlign: 'center',
			cellWidthOverride: '6',
			initialSortDirection: 'desc',
		},
		{
			title: 'LNG',
			dataKey: 'longestPass',
			textAlign: 'center',
			cellWidthOverride: '6',
			initialSortDirection: 'desc',
		},
		{
			title: 'TD',
			dataKey: 'touchdowns',
			textAlign: 'center',
			cellWidthOverride: '6',
			initialSortDirection: 'desc',
		},
		{
			title: 'QBR',
			dataKey: 'quarterbackRating',
			textAlign: 'center',
			cellWidthOverride: '6',
			initialSortDirection: 'desc',
		},
		{
			title: 'INT',
			dataKey: 'interceptions',
			textAlign: 'center',
			cellWidthOverride: '6',
			initialSortDirection: 'asc',
		},
		{
			title: 'SACK',
			dataKey: 'totalSacks',
			textAlign: 'center',
			cellWidthOverride: '6',
			initialSortDirection: 'asc',
		},
		{
			title: 'F-FL',
			dataKey: 'fumblesLost',
			textAlign: 'center',
			cellWidthOverride: '6',
			initialSortDirection: 'asc',
		},
	];

	const passingData = stats.map(player => {
		return {
			player: formatPlayerLink(player, { position: player.position }),
			gamesPlayed: player.games_played,
			completions: player.completions,
			attempts: player.passing_attempts,
			completionPct: player.completion_pct,
			passingYards: player.passing_yards,
			averageYards: player.passing_yards_per_attempt,
			yardsPerGame: player.yards_per_game,
			longestPass: player.longest_pass,
			touchdowns: player.passing_touchdowns,
			quarterbackRating: parseFloat(player.total_qbr),
			interceptions: player.interceptions,
			totalSacks: player.total_sacks,
			fumblesLost: `${player.passing_fumbles}-${player.passing_fumbles_lost}`,
		};
	});

	return {
		columns: passingColumns,
		data: passingData,
	};
}

function formatRushingStats(stats = []) {
	const rushingColumns = [
		{
			title: 'PLAYER',
			dataKey: 'player',
			textAlign: 'left',
			cellWidthOverride: '20',
			customSortFunctionAscending: (a, b) => sortByPlayerElement(a.player, b.player),
			customSortFunctionDescending: (a, b) => sortByPlayerElement(b.player, a.player),
			initialSortDirection: 'asc',
			sticky: true,
		},
		{
			title: 'GP',
			dataKey: 'gamesPlayed',
			textAlign: 'center',
			cellWidthOverride: '8',
			initialSortDirection: 'desc',
		},
		{
			title: 'ATT',
			dataKey: 'attempts',
			textAlign: 'center',
			cellWidthOverride: '8',
			initialSortDirection: 'desc',
		},
		{
			title: 'YDS',
			dataKey: 'rushingYards',
			textAlign: 'center',
			cellWidthOverride: '8',
			initialSortDirection: 'desc',
		},
		{
			title: 'AVG',
			dataKey: 'averageYards',
			textAlign: 'center',
			cellWidthOverride: '8',
			initialSortDirection: 'desc',
		},
		{
			title: 'YDS/G',
			dataKey: 'rushingYardsPerGame',
			textAlign: 'center',
			cellWidthOverride: '8',
			customSortFunctionAscending: (a, b) => sortByNumber(a.rushingYardsPerGame, b.rushingYardsPerGame),
			customSortFunctionDescending: (a, b) => sortByNumber(b.rushingYardsPerGame, a.rushingYardsPerGame),
			initialSortDirection: 'desc',
		},
		{
			title: 'LNG',
			dataKey: 'longestRush',
			textAlign: 'center',
			cellWidthOverride: '8',
			initialSortDirection: 'desc',
		},
		{
			title: 'TD',
			dataKey: 'touchdowns',
			textAlign: 'center',
			cellWidthOverride: '8',
			initialSortDirection: 'desc',
		},
		{
			title: 'FD',
			dataKey: 'firstDowns',
			textAlign: 'center',
			cellWidthOverride: '8',
			initialSortDirection: 'desc',
		},
		{
			title: 'F-FL',
			dataKey: 'fumblesLost',
			textAlign: 'center',
			cellWidthOverride: '8',
			initialSortDirection: 'asc',
		},
	];

	const rushingData = stats.map(player => {
		return {
			player: formatPlayerLink(player, { position: player.position }),
			gamesPlayed: player.games_played,
			attempts: player.rushing_attempts,
			rushingYards: player.rushing_yards,
			averageYards: player.runners_yards_per_attempt,
			rushingYardsPerGame: player.rushing_yards_per_game,
			longestRush: player.long_rushing,
			touchdowns: player.rushing_touchdowns,
			firstDowns: player.rushing_first_downs,
			fumblesLost: `${player.rushing_fumbles}-${player.rushing_fumbles_lost}`,
		};
	});

	return {
		columns: rushingColumns,
		data: rushingData,
	};
}

function formatReceivingStats(stats = []) {
	const receivingColumns = [
		{
			title: 'PLAYER',
			dataKey: 'player',
			textAlign: 'left',
			cellWidthOverride: '20',
			customSortFunctionAscending: (a, b) => sortByPlayerElement(a.player, b.player),
			customSortFunctionDescending: (a, b) => sortByPlayerElement(b.player, a.player),
			initialSortDirection: 'asc',
			sticky: true,
		},
		{
			title: 'GP',
			dataKey: 'gamesPlayed',
			textAlign: 'center',
			cellWidthOverride: '8',
			initialSortDirection: 'desc',
		},
		{
			title: 'REC',
			dataKey: 'receptions',
			textAlign: 'center',
			cellWidthOverride: '8',
			initialSortDirection: 'desc',
		},
		{
			title: 'YDS',
			dataKey: 'receivingYards',
			textAlign: 'center',
			cellWidthOverride: '8',
			initialSortDirection: 'desc',
		},
		{
			title: 'TGTS',
			dataKey: 'receivingTargets',
			textAlign: 'center',
			cellWidthOverride: '8',
			initialSortDirection: 'desc',
		},
		{
			title: 'AVG',
			dataKey: 'averageYards',
			textAlign: 'center',
			cellWidthOverride: '8',
			initialSortDirection: 'desc',
		},
		{
			title: 'YDS/G',
			dataKey: 'receivingYardsPerGame',
			textAlign: 'center',
			cellWidthOverride: '8',
			customSortFunctionAscending: (a, b) => sortByNumber(a.receivingYardsPerGame, b.receivingYardsPerGame),
			customSortFunctionDescending: (a, b) => sortByNumber(b.receivingYardsPerGame, a.receivingYardsPerGame),
			initialSortDirection: 'desc',
		},
		{
			title: 'LNG',
			dataKey: 'longReception',
			textAlign: 'center',
			cellWidthOverride: '8',
			initialSortDirection: 'desc',
		},
		{
			title: 'TD',
			dataKey: 'touchdowns',
			textAlign: 'center',
			cellWidthOverride: '8',
			initialSortDirection: 'desc',
		},
		{
			title: 'FD',
			dataKey: 'firstDowns',
			textAlign: 'center',
			cellWidthOverride: '8',
			initialSortDirection: 'desc',
		},
		{
			title: 'F-FL',
			dataKey: 'fumblesLost',
			textAlign: 'center',
			cellWidthOverride: '8',
			initialSortDirection: 'asc',
		},
	];

	const receivingData = stats.map(player => {
		return {
			player: formatPlayerLink(player, { position: player.position }),
			gamesPlayed: player.games_played,
			receptions: player.receptions,
			receivingYards: player.receiving_yards,
			receivingTargets: player.receiving_targets,
			averageYards: player.yards_per_reception,
			receivingYardsPerGame: player.receivers_yards_per_game,
			longReception: player.long_reception,
			touchdowns: player.receiving_touchdowns,
			firstDowns: player.receiving_first_downs,
			fumblesLost: `${player.receiving_fumbles}-${player.receiving_fumbles_lost}`,
		};
	});

	return {
		columns: receivingColumns,
		data: receivingData,
	};
}

function formatDefenseStats(stats = []) {
	const defenceColumns = [
		{
			title: 'PLAYER',
			dataKey: 'player',
			textAlign: 'left',
			cellWidthOverride: '20',
			customSortFunctionAscending: (a, b) => sortByPlayerElement(a.player, b.player),
			customSortFunctionDescending: (a, b) => sortByPlayerElement(b.player, a.player),
			initialSortDirection: 'asc',
			sticky: true,
		},
		{
			title: 'GP',
			dataKey: 'gamesPlayed',
			textAlign: 'center',
			cellWidthOverride: '8',
			initialSortDirection: 'desc',
		},
		{
			title: 'TOTAL',
			dataKey: 'totalTackles',
			textAlign: 'center',
			cellWidthOverride: '8',
			initialSortDirection: 'desc',
		},
		{
			title: 'SOLO',
			dataKey: 'soloTackles',
			textAlign: 'center',
			cellWidthOverride: '8',
			initialSortDirection: 'desc',
		},
		{
			title: 'AST',
			dataKey: 'assistTackles',
			textAlign: 'center',
			cellWidthOverride: '8',
			initialSortDirection: 'desc',
		},
		{
			title: 'SACK',
			dataKey: 'defenseSacks',
			textAlign: 'center',
			cellWidthOverride: '8',
			initialSortDirection: 'desc',
		},
		{
			title: 'INT',
			dataKey: 'defenseInterceptions',
			textAlign: 'center',
			cellWidthOverride: '8',
			initialSortDirection: 'desc',
		},
		{
			title: 'PDEF',
			dataKey: 'passesDefended',
			textAlign: 'center',
			cellWidthOverride: '8',
			initialSortDirection: 'desc',
		},
		{
			title: 'FF',
			dataKey: 'forcedFumbles',
			textAlign: 'center',
			cellWidthOverride: '8',
			initialSortDirection: 'desc',
		},
		{
			title: 'REC',
			dataKey: 'fumblesRecovered',
			textAlign: 'center',
			cellWidthOverride: '8',
			initialSortDirection: 'desc',
		},
	];

	const defenceData = stats.map(player => {
		return {
			player: formatPlayerLink(player, { position: player.position }),
			gamesPlayed: player.games_played,
			totalTackles: player.total_tackles,
			soloTackles: player.solo_tackles,
			assistTackles: player.assist_tackles,
			defenseSacks: player.defense_sacks,
			defenseInterceptions: player.defense_interceptions,
			passesDefended: player.passes_defended,
			forcedFumbles: player.forced_fumbles,
			fumblesRecovered: player.fumbles_recovered,
		};
	});

	return {
		columns: defenceColumns,
		data: defenceData,
	};
}

function formatKickingStats(stats = []) {
	const kickingColumns = [
		{
			title: 'PLAYER',
			dataKey: 'player',
			textAlign: 'left',
			cellWidthOverride: '30',
			customSortFunctionAscending: (a, b) => sortByPlayerElement(a.player, b.player),
			customSortFunctionDescending: (a, b) => sortByPlayerElement(b.player, a.player),
			initialSortDirection: 'asc',
			sticky: true,
		},
		{
			title: 'GP',
			dataKey: 'gamesPlayed',
			textAlign: 'center',
			cellWidthOverride: '10',
			initialSortDirection: 'desc',
		},
		{
			title: 'FGM-FGA',
			dataKey: 'fieldGoals',
			textAlign: 'center',
			cellWidthOverride: '10',
			initialSortDirection: 'desc',
		},
		{
			title: 'PCT',
			dataKey: 'fieldGoalsPct',
			textAlign: 'center',
			cellWidthOverride: '10',
			initialSortDirection: 'desc',
		},
		{
			title: 'LNG',
			dataKey: 'longFieldGoalMade',
			textAlign: 'center',
			cellWidthOverride: '10',
			initialSortDirection: 'desc',
		},
		{
			title: '50+',
			dataKey: 'fiftyPlus',
			textAlign: 'center',
			cellWidthOverride: '10',
			initialSortDirection: 'desc',
		},
		{
			title: 'XPM-XPA',
			dataKey: 'extraPoints',
			textAlign: 'center',
			cellWidthOverride: '10',
			initialSortDirection: 'desc',
		},
		{
			title: 'XP%',
			dataKey: 'extraPointsPct',
			textAlign: 'center',
			cellWidthOverride: '10',
			initialSortDirection: 'desc',
		},
	];

	const kickingData = stats.map(player => {
		return {
			player: formatPlayerLink(player, { position: player.position }),
			gamesPlayed: player.games_played,
			fieldGoals: `${player.field_goals_made}-${player.field_goals_attempts}`,
			fieldGoalsPct: player.field_goals_pct,
			longFieldGoalMade: player.long_field_goal_made,
			fiftyPlus: player.field_goals_50_plus,
			extraPoints: `${player.extra_points_made}-${player.extra_points_attempts}`,
			extraPointsPct: player.extra_points_pct,
		};
	});

	return {
		columns: kickingColumns,
		data: kickingData,
	};
}

function formatPuntingStats(stats = []) {
	const puntingColumns = [
		{
			title: 'PLAYER',
			dataKey: 'player',
			textAlign: 'left',
			cellWidthOverride: '20',
			customSortFunctionAscending: (a, b) => sortByPlayerElement(a.player, b.player),
			customSortFunctionDescending: (a, b) => sortByPlayerElement(b.player, a.player),
			initialSortDirection: 'asc',
			sticky: true,
		},
		{
			title: 'GP',
			dataKey: 'gamesPlayed',
			textAlign: 'center',
			cellWidthOverride: '8',
			initialSortDirection: 'desc',
		},
		{
			title: 'P',
			dataKey: 'punts',
			textAlign: 'center',
			cellWidthOverride: '8',
			initialSortDirection: 'desc',
		},
		{
			title: 'YDS',
			dataKey: 'puntYards',
			textAlign: 'center',
			cellWidthOverride: '8',
			initialSortDirection: 'desc',
		},
		{
			title: 'AVG',
			dataKey: 'averagePunts',
			textAlign: 'center',
			cellWidthOverride: '8',
			initialSortDirection: 'desc',
		},
		{
			title: 'LNG',
			dataKey: 'longPunts',
			textAlign: 'center',
			cellWidthOverride: '8',
			initialSortDirection: 'desc',
		},
		{
			title: 'NET',
			dataKey: 'netPunts',
			textAlign: 'center',
			cellWidthOverride: '8',
			initialSortDirection: 'desc',
		},
		{
			title: 'TB',
			dataKey: 'touchbacks',
			textAlign: 'center',
			cellWidthOverride: '8',
			initialSortDirection: 'desc',
		},
		{
			title: 'FC',
			dataKey: 'fairCatches',
			textAlign: 'center',
			cellWidthOverride: '8',
			initialSortDirection: 'desc',
		},
		{
			title: 'IN20',
			dataKey: 'puntsInside',
			textAlign: 'center',
			cellWidthOverride: '8',
			initialSortDirection: 'desc',
		},
	];

	const puntingData = stats.map(player => {
		return {
			player: formatPlayerLink(player, { position: player.position }),
			gamesPlayed: player.games_played,
			punts: player.punts,
			puntYards: player.punt_yards,
			averagePunts: player.punt_yards_avg,
			longPunts: player.long_punts,
			netPunts: player.net_average_punt_yards,
			touchbacks: player.touchbacks,
			fairCatches: player.fair_catches,
			puntsInside: player.punts_inside_20,
		};
	});

	return {
		columns: puntingColumns,
		data: puntingData,
	};
}

function formatReturningStats(stats = []) {
	const returningColumns = [
		{
			title: 'PLAYER',
			dataKey: 'player',
			textAlign: 'left',
			cellWidthOverride: '20',
			customSortFunctionAscending: (a, b) => sortByPlayerElement(a.player, b.player),
			customSortFunctionDescending: (a, b) => sortByPlayerElement(b.player, a.player),
			initialSortDirection: 'asc',
			sticky: true,
		},
		{
			title: 'GP',
			dataKey: 'gamesPlayed',
			textAlign: 'center',
			cellWidthOverride: '7',
			initialSortDirection: 'desc',
		},
		{
			title: 'PR',
			dataKey: 'puntReturn',
			textAlign: 'center',
			cellWidthOverride: '7',
			initialSortDirection: 'desc',
		},
		{
			title: 'YDS',
			dataKey: 'puntReturnYards',
			textAlign: 'center',
			cellWidthOverride: '7',
			initialSortDirection: 'desc',
		},
		{
			title: 'TD',
			dataKey: 'puntTouchdowns',
			textAlign: 'center',
			cellWidthOverride: '7',
			initialSortDirection: 'desc',
		},
		{
			title: 'LNG',
			dataKey: 'longestPuntReturn',
			textAlign: 'center',
			cellWidthOverride: '7',
			initialSortDirection: 'desc',
		},
		{
			title: 'AVG',
			dataKey: 'puntAvg',
			textAlign: 'center',
			cellWidthOverride: '7',
			customSortFunctionAscending: (a, b) => sortByNumber(a.puntAvg, b.puntAvg),
			customSortFunctionDescending: (a, b) => sortByNumber(b.puntAvg, a.puntAvg),
			initialSortDirection: 'desc',
		},
		{
			title: 'KR',
			dataKey: 'kickoffReturn',
			textAlign: 'center',
			cellWidthOverride: '7',
			initialSortDirection: 'desc',
		},
		{
			title: 'YDS',
			dataKey: 'kickoffReturnYards',
			textAlign: 'center',
			cellWidthOverride: '7',
			initialSortDirection: 'desc',
		},
		{
			title: 'TD',
			dataKey: 'kickoffTouchdowns',
			textAlign: 'center',
			cellWidthOverride: '7',
			initialSortDirection: 'desc',
		},
		{
			title: 'LNG',
			dataKey: 'longestKickoffReturn',
			textAlign: 'center',
			cellWidthOverride: '7',
			initialSortDirection: 'desc',
		},
		{
			title: 'AVG',
			dataKey: 'kickoffAvg',
			textAlign: 'center',
			cellWidthOverride: '7',
			initialSortDirection: 'desc',
		},
	];

	const returningData = stats.map(player => {
		return {
			player: formatPlayerLink(player, { position: player.position }),
			gamesPlayed: player.games_played,
			puntReturn: player.punt_returning_returns,
			puntReturnYards: player.punt_returning_yards,
			puntTouchdowns: player.punt_returning_touchdowns,
			longestPuntReturn: player.punt_returning_long_yards,
			puntAvg: player.punt_returning_avg,

			kickoffReturn: player.kickoff_returning_returns,
			kickoffReturnYards: player.kickoff_returning_yards,
			kickoffTouchdowns: player.kickoff_returning_touchdowns,
			longestKickoffReturn: player.kickoff_returning_long_yards,
			kickoffAvg: player.kickoff_returning_avg,
		};
	});

	return {
		columns: returningColumns,
		data: returningData,
	};
}

export function formatFullSeasonStats(stats) {
	return [
		{
			title: 'Passing',
			initialSortColumn: 'passingYards',
			initialSortReverse: true,
			...formatPassingStats(stats?.passing),
		},
		{
			title: 'Rushing',
			initialSortColumn: 'rushingYards',
			initialSortReverse: true,
			...formatRushingStats(stats?.rushing),
		},
		{
			title: 'Receiving',
			initialSortColumn: 'receivingYards',
			initialSortReverse: true,
			...formatReceivingStats(stats?.receiving),
		},
		{
			title: 'Defense',
			initialSortColumn: 'totalTackles',
			initialSortReverse: true,
			...formatDefenseStats(stats?.defense),
		},
		{
			title: 'Kicking',
			initialSortColumn: 'fieldGoalsPct',
			initialSortReverse: true,
			...formatKickingStats(stats?.kicking),
		},
		{
			title: 'Punting',
			initialSortColumn: 'puntYards',
			initialSortReverse: true,
			...formatPuntingStats(stats?.punting),
		},
		{
			title: 'Returning',
			initialSortColumn: 'puntReturnYards',
			initialSortReverse: true,
			...formatReturningStats(stats?.returning),
		},
	];
}
